/**
 * 월간 보고서 한 장 — 보고서 이미지(`reportCard/template.ts`)와 같은 순서·같은 숫자를 웹으로 그린다(261030-report-auto-dispatch).
 * 공개 화면(`/r`)과 운영자 미리보기가 같이 쓴다. **휴대폰이 기준**이다(알림톡 버튼으로 연다).
 *
 * ★ 표본(k/n)·신뢰구간 숫자를 싣지 않는다 — 그래프의 띠만 그린다(260921-client-report-chart-only). 데이터에 원래 없다.
 * ★ 숫자는 서버가 발송 때 고정한 값 그대로다 — 화면이 다시 계산하지 않는다.
 */
import type { ReportPageView } from '@geo-admin/shared/contract/api/reportDispatch.ts';
import { LogoMark } from './LogoMark.tsx';

type Work = ReportPageView['works'][number];
type Highlight = ReportPageView['highlights'][number];
type Query = NonNullable<ReportPageView['result']>['queries'][number];

const ICON: Record<Work['key'], string> = {
  post: 'M7 3h7l4 4v14H7z M14 3v4h4 M10 12h5 M10 15.5h5 M10 9h2',
  press: 'M4 4h16v16H4z M8 8h8 M8 12h8 M8 16h5',
  video: 'M3.5 5h17v14h-17z M10.5 9.5v5l4-2.5z',
  homepage: 'M3 4h18v13H3z M8 21h8 M12 17v4 M3 8.5h18',
  review: 'M4 5h16v11H9l-5 4z M8 9.5h8 M8 12.5h5',
};
const TAG: Record<Highlight['kind'], string> = {
  press: '언론 기사',
  video: '숏폼',
  post: '홈페이지 글',
  review: '블로그',
};

const md = (iso: string) => `${Number(iso.slice(5, 7))}월 ${Number(iso.slice(8, 10))}일`;

function WorkRow({ w }: { w: Work }) {
  return (
    <li className="flex items-center gap-3.5 py-4">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#F1F5FF]">
        <svg viewBox="0 0 24 24" className="size-6" aria-hidden="true">
          <path
            d={ICON[w.key]}
            fill="none"
            stroke="#0B1736"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-bold text-[#0B1736] text-[15px] leading-snug">{w.label}</span>
        {w.detail ? <span className="mt-0.5 block text-[#5A6785] text-[13px]">{w.detail}</span> : null}
      </span>
      <span className="shrink-0 font-black text-[#1E5BFF]">
        <span className="text-[28px] tracking-tight">{w.n}</span>
        <span className="ml-0.5 text-base">{w.unit}</span>
      </span>
    </li>
  );
}

function Thumb({ h, className }: { h: Highlight; className: string }) {
  return h.image ? (
    <img src={h.image} alt="" className={`${className} object-cover`} />
  ) : (
    <div className={`${className} flex items-center justify-center bg-[#EAF1FF] text-[#1E5BFF]/50 text-xs`}>
      {TAG[h.kind]}
    </div>
  );
}

function Highlights({ items }: { items: Highlight[] }) {
  const press = items.filter((h) => h.kind === 'press');
  const tiles = items.filter((h) => h.kind !== 'press');
  return (
    <div className="space-y-3">
      {press.map((h) => (
        <article
          key={h.title}
          className="overflow-hidden rounded-2xl bg-white p-2.5 shadow-[0_6px_24px_rgba(16,40,100,.06)]"
        >
          <Thumb h={h} className="aspect-video w-full rounded-xl" />
          <div className="px-1.5 pt-3 pb-1.5">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-[#E3ECFF] px-2.5 py-0.5 font-bold text-[#1E5BFF] text-xs">
                언론 기사
              </span>
              {h.source ? <span className="text-[#5A6785] text-xs">{h.source}</span> : null}
            </div>
            <h3 className="mt-2 line-clamp-2 font-extrabold text-[#0B1736] text-[17px] leading-snug">{h.title}</h3>
            {h.excerpt ? (
              <p className="mt-1.5 line-clamp-3 text-[#5A6785] text-sm leading-relaxed">{h.excerpt}</p>
            ) : null}
          </div>
        </article>
      ))}
      {tiles.length ? (
        <div className="grid grid-cols-3 gap-2.5">
          {tiles.map((h) => (
            <article key={h.title} className="rounded-xl bg-white p-1.5 shadow-[0_6px_24px_rgba(16,40,100,.06)]">
              <Thumb h={h} className="aspect-[9/10] w-full rounded-lg" />
              <div className="px-0.5 pt-2 pb-1">
                <span className="rounded-full bg-[#E3ECFF] px-2 py-0.5 font-bold text-[#1E5BFF] text-[10px]">
                  {TAG[h.kind]}
                </span>
                <p className="mt-1.5 line-clamp-2 font-bold text-[#0B1736] text-xs leading-snug">{h.title}</p>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** 추이 — 띠(lo~hi) · 선 · 점. 글자는 SVG 밖(눈금)에 쓴다 */
function Trend({ q, days, ticks }: { q: Query; days: number; ticks: { index: number; label: string }[] }) {
  const W = 320;
  const H = 120;
  const pts = q.series.map((p, i) => (p ? { i, ...p } : null)).filter((p) => p !== null);
  if (days <= 0 || pts.length === 0)
    return <p className="py-6 text-center text-sm text-white/80">아직 기록이 없습니다</p>;
  const top = Math.min(100, Math.max(10, ...pts.map((p) => Math.max(p.hi, p.rate))) * 1.15);
  const x = (i: number) => (days <= 1 ? W / 2 : (i / (days - 1)) * (W - 12) + 6);
  const y = (v: number) => H - 6 - (v / top) * (H - 12);
  const band = [...pts.map((p) => `${x(p.i)},${y(p.hi)}`), ...[...pts].reverse().map((p) => `${x(p.i)},${y(p.lo)}`)];
  const last = pts.at(-1)!;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" aria-hidden="true">
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="rgba(255,255,255,.14)" />
        ))}
        <polygon points={band.join(' ')} fill="#fff" fillOpacity=".18" />
        <polyline
          points={pts.map((p) => `${x(p.i)},${y(p.rate)}`).join(' ')}
          fill="none"
          stroke="#fff"
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={x(last.i)} cy={y(last.rate)} r="5" fill="#1E5BFF" stroke="#fff" strokeWidth="3" />
      </svg>
      <div className="relative mt-1 h-4 text-[11px] text-white/75">
        {ticks.map((t) => (
          <span key={t.index} className="-translate-x-1/2 absolute" style={{ left: `${(x(t.index) / W) * 100}%` }}>
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function RateCard({ result }: { result: NonNullable<ReportPageView['result']> }) {
  return (
    <section className="rounded-3xl bg-gradient-to-br from-[#2F6BFF] to-[#1748E6] p-5 text-white shadow-[0_20px_50px_rgba(30,91,255,.25)]">
      <h2 className="font-extrabold text-lg">ChatGPT 브랜드 언급률</h2>
      <div className="mt-4 space-y-6">
        {result.queries.map((q) => (
          <div key={q.text}>
            <p className="font-semibold text-sm text-white/90">“{q.text}”</p>
            {q.headline ? (
              <div className="mt-2">
                <p className="font-black text-[40px] leading-none tracking-tight">
                  {q.headline.from !== null && q.headline.from < q.headline.value ? (
                    <>
                      <span className="text-white/60 text-[28px]">{q.headline.from}%</span>
                      <span className="mx-2 text-[24px] text-white/70">→</span>
                    </>
                  ) : null}
                  {q.headline.value}%
                </p>
                <p className="mt-1.5 text-white/75 text-xs">{q.headline.caption}</p>
              </div>
            ) : (
              <p className="mt-2 text-sm text-white/80">측정을 준비하고 있습니다</p>
            )}
            <div className="mt-4">
              <Trend q={q} days={result.days} ticks={result.ticks} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ReportPage({ view }: { view: ReportPageView }) {
  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-[#F6F9FF] to-[#EEF4FF] font-suit">
      <main className="mx-auto w-full max-w-md px-4 pt-7 pb-12">
        <header className="flex items-center justify-between">
          <LogoMark tone="onLight" size="sm" />
          <span className="text-[#5A6785] text-[11px] tracking-[0.3em]">MONTHLY REPORT</span>
        </header>
        <div className="mt-7">
          <h1 className="break-keep font-black text-[#0B1736] text-[32px] leading-tight tracking-tight">
            {view.brandName}
          </h1>
          <p className="mt-1 break-keep font-black text-[24px] tracking-tight">
            <span className="text-[#1E5BFF]">{view.monthLabel}</span>
            <span className="ml-2 text-[#0B1736]">AI 검색최적화 보고서</span>
          </p>
          <p className="mt-2 text-[#5A6785] text-sm tracking-wide">{view.period}</p>
        </div>

        {view.works.length ? (
          <section className="mt-8">
            <h2 className="mb-3 font-extrabold text-[#0B1736] text-lg">이번 달 진행 내역</h2>
            <ul className="divide-y divide-[#E6ECF8] rounded-2xl bg-white px-4 shadow-[0_6px_24px_rgba(16,40,100,.06)]">
              {view.works.map((w) => (
                <WorkRow key={w.key} w={w} />
              ))}
            </ul>
          </section>
        ) : null}

        {view.highlights.length ? (
          <section className="mt-8">
            <h2 className="mb-3 font-extrabold text-[#0B1736] text-lg">콘텐츠 하이라이트</h2>
            <Highlights items={view.highlights} />
          </section>
        ) : null}

        {view.result ? (
          <div className="mt-8">
            <RateCard result={view.result} />
          </div>
        ) : null}

        <p className="mt-8 rounded-2xl bg-[#0B1736] px-5 py-4 text-center font-bold text-sm text-white leading-relaxed">
          {view.kind === 'renewal'
            ? `${md(view.nextOn)}부터 다음 회차 구독이 이어집니다.`
            : `구독은 ${md(view.nextOn)}로 끝납니다. 이용해 주셔서 감사합니다.`}
        </p>
      </main>
    </div>
  );
}
